import { useCallback, useMemo } from 'react'

import { Tooltip } from '@mantine/core'

import { cn } from 'src/utils'

import {
  DAY_LABELS,
  DAY_ORDER,
  HOURS,
  hourLabel,
  type HeatmapCell,
} from './constants'
import { binClass } from './heatmapBins'

const zeroCell = (dayOfWeek: number, hour: number): HeatmapCell => ({
  dayOfWeek,
  hour,
  calls: 0,
  voiceAgentCalls: 0,
  humanCalls: 0,
  bookings: 0,
})

export interface HeatmapGridProps {
  cells: ReadonlyArray<HeatmapCell>
  /**
   * Resolves the value shown for a cell under the active actor view. The grid
   * re-derives its lookup, peak labels, and per-day totals from this, so
   * switching All/Agent/Employee recolors and retotals the whole grid.
   */
  getValue: (cell: HeatmapCell) => number
}

/**
 * The 7×24 volume grid: weekday rows (Mon-first) × local hour, with the
 * staffed 9am–5pm window marked by a brand header track + dashed rails, the
 * 1–2 hottest cells labeled inline, and a per-day totals column.
 */
export const HeatmapGrid = ({ cells, getValue }: HeatmapGridProps) => {
  const cellLookup = useMemo(() => {
    const lookup = new Map<string, HeatmapCell>()
    cells.forEach((cell) => lookup.set(`${cell.dayOfWeek}__${cell.hour}`, cell))
    return lookup
  }, [cells])

  const cellAt = useCallback(
    (dayOfWeek: number, hour: number): HeatmapCell =>
      cellLookup.get(`${dayOfWeek}__${hour}`) ?? zeroCell(dayOfWeek, hour),
    [cellLookup]
  )

  const valueAt = useCallback(
    (dayOfWeek: number, hour: number): number =>
      getValue(cellAt(dayOfWeek, hour)),
    [cellAt, getValue]
  )

  // The 1–2 hottest cells get a direct label on the grid so the takeaway
  // doesn't depend on hovering. Skipped when the grid is empty or too sparse
  // to have a meaningful peak.
  const peakKeys = useMemo(() => {
    const ranked = cells
      .map((cell) => ({ cell, value: getValue(cell) }))
      .sort((a, b) => b.value - a.value)
    if (ranked.length === 0 || (ranked[0]?.value ?? 0) < 2) {
      return new Set<string>()
    }
    return new Set(
      ranked
        .slice(0, 2)
        .filter(({ value }) => value >= 2)
        .map(({ cell }) => `${cell.dayOfWeek}__${cell.hour}`)
    )
  }, [cells, getValue])

  const dayTotals = useMemo(() => {
    const totals = new Map<number, number>()
    DAY_ORDER.forEach((dayOfWeek) => {
      totals.set(
        dayOfWeek,
        HOURS.reduce((sum, hour) => sum + valueAt(dayOfWeek, hour), 0)
      )
    })
    return totals
  }, [valueAt])

  return (
    <table className="w-full table-fixed border-separate border-spacing-[3px] text-[11px]">
      <thead>
        <tr>
          <th className="w-9" />
          {HOURS.map((hour) => (
            <th
              key={hour}
              className={cn(
                'relative pb-1 text-center text-[10px] font-medium',
                hour === 9 || hour === 17
                  ? 'font-bold text-doubleNickel-brand-600'
                  : 'text-doubleNickel-gray-500'
              )}
            >
              {hourLabel(hour)}
              {/* Header track: a continuous brand underline spanning the
                  staffed 9am-5pm window. Bleeds into the border-spacing gaps
                  so segments join and the bar runs to the 5pm boundary. */}
              {hour >= 9 && hour < 17 && (
                <span
                  className="absolute bottom-0 h-[2px] bg-doubleNickel-brand-500"
                  style={{
                    left: hour === 9 ? 0 : -3,
                    right: hour === 16 ? -3 : 0,
                  }}
                  data-testid={`heatmap-track-${hour}`}
                />
              )}
            </th>
          ))}
          <th className="w-12 pl-1.5 text-right text-[10px] font-medium text-doubleNickel-gray-600">
            Total Calls
          </th>
        </tr>
      </thead>
      <tbody>
        {DAY_ORDER.map((dayOfWeek) => {
          const weekday = dayOfWeek >= 1 && dayOfWeek <= 5
          return (
            <tr key={dayOfWeek}>
              <td className="pr-1.5 text-xs font-medium text-doubleNickel-gray-700">
                {DAY_LABELS[dayOfWeek]}
              </td>
              {HOURS.map((hour) => {
                const value = valueAt(dayOfWeek, hour)
                const key = `${dayOfWeek}__${hour}`
                const isPeak = peakKeys.has(key)
                return (
                  <td
                    key={hour}
                    className={cn(
                      'relative h-6 p-0',
                      // Soft rails: light dashed boundaries on weekday rows at
                      // the left edges of the 9am and 5pm columns, so both
                      // rails land exactly under their bold header labels.
                      weekday &&
                        (hour === 9 || hour === 17) &&
                        'border-l border-dashed border-doubleNickel-brand-200'
                    )}
                    data-testid={`heatmap-cell-${key}`}
                    data-value={value}
                  >
                    {isPeak && (
                      <span
                        className="absolute -top-[17px] left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded border border-doubleNickel-gray-300 bg-white px-1 font-sans text-[9px] font-semibold leading-[14px] text-doubleNickel-gray-900 shadow-sm"
                        data-testid={`heatmap-peak-${key}`}
                      >
                        {DAY_LABELS[dayOfWeek]} {hourLabel(hour)} · {value}
                      </span>
                    )}
                    <Tooltip
                      position="top"
                      offset={2}
                      classNames={{
                        tooltip:
                          'rounded-md border border-gray-200 bg-white px-3 py-2 text-[11px] shadow-md',
                      }}
                      label={
                        <span className="text-doubleNickel-gray-800">
                          {DAY_LABELS[dayOfWeek]} {hourLabel(hour)} ·{' '}
                          <span className="font-semibold tabular-nums text-doubleNickel-gray-900">
                            {value}
                          </span>{' '}
                          {value === 1 ? 'call' : 'calls'}
                        </span>
                      }
                    >
                      <div
                        className={cn(
                          'h-6 rounded',
                          binClass(value),
                          isPeak &&
                            'ring-1 ring-inset ring-doubleNickel-gray-900'
                        )}
                      />
                    </Tooltip>
                  </td>
                )
              })}
              <td
                className="pl-1.5 text-right text-xs font-semibold tabular-nums text-doubleNickel-gray-900"
                data-testid={`heatmap-day-total-${dayOfWeek}`}
              >
                {(dayTotals.get(dayOfWeek) ?? 0).toLocaleString()}
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

export default HeatmapGrid
